import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getPromotionOptions, getPromotionDictionary, mapPricesToPromotions } from '../services/apiService';
import { splitPromoNames } from '../utils/promotion';
import { mirrorPriceMapping } from '../services/promotionMirrorApi';
import { getUserInfo } from '../utils/tokenManager';

/* 기획전 매핑 모달
 * ===========================================================================
 * 선택한 날짜(가격번호)들을 기획전에 매핑한다. 가격/재고 화면 3종(투어·호텔·일반)이
 * 공유할 수 있도록 캘린더 구현에 의존하지 않고 `selection` 배열만 받는다.
 *
 * 교체 시맨틱
 * ---------------------------------------------------------------------------
 * RCT_T_상품가격_기획전연동은 선택 가격번호의 기존 매핑을 전부 DELETE한 뒤
 * (가격번호 × 기획전) CROSS JOIN으로 다시 INSERT한다. 즉 저장하면 선택한 모든
 * 날짜의 기획전 소속이 "동일해진다". 부분 적용 상태는 보존할 수 없으므로
 * 3-상태 체크박스로 현재 상태를 보여주되 전체적용/전체해제만 허용하고,
 * 결과를 경고문으로 명시한다.
 *
 * 번호 확정과 fail-closed
 * ---------------------------------------------------------------------------
 * 캘린더가 가진 기존 매핑 정보는 기획전"명" 문자열(STRING_AGG(기획전명,','))뿐이다.
 * 번호를 얻으려면 RCT_S_기획전(전 기간·마감 0/1) 사전으로 역매핑해야 하는데,
 * 아래 세 경우는 번호를 확정할 수 없거나 저장 시 데이터가 사라진다.
 * 해당 날짜는 저장 대상에서 제외한다(조용한 소실 방지).
 *
 *   no-price   가격이 등록되지 않아 가격번호가 없다
 *   unresolved 동명 기획전이거나 이름에 콤마가 있어 번호를 확정할 수 없다
 *   locked     선택 목록에 없는 기획전(마감된 기획전 등)에 속해 있다
 *              → 저장하면 DELETE가 그 매핑을 지우므로 아예 건드리지 않는다
 */

const BLOCK_LABEL = {
  'no-price': '가격 미등록 (가격번호 없음)',
  unresolved: '기획전명으로 번호를 확정할 수 없음 (동명 또는 이름에 콤마)',
  locked: '선택 목록에 없는 기획전(마감 등)에 속함',
};

const PromotionMappingModal = ({ open, onClose, productNo, productName = '', selection = [], onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [options, setOptions] = useState([]);
  const [dictRows, setDictRows] = useState([]);
  const [checked, setChecked] = useState(() => new Set());
  // 사용자가 직접 건드린 항목. 부분 적용(-) 표시를 "아직 결정 안 함"으로만 쓰기 위해 필요하다.
  const [touched, setTouched] = useState(() => new Set());
  const [showBlocked, setShowBlocked] = useState(false);
  // 열려 있는 동안 체크 상태를 다시 초기화하지 않기 위한 가드
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const [opts, dict] = await Promise.all([getPromotionOptions(), getPromotionDictionary()]);
        if (!alive) return;
        setOptions(Array.isArray(opts) ? opts : []);
        setDictRows(Array.isArray(dict) ? dict : []);
      } catch (e) {
        if (alive) setError(e?.message || '기획전 목록을 불러오지 못했습니다.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  // 기획전명 → 번호 목록. 2개 이상이면 그 이름은 확정 불가.
  const nameToNos = useMemo(() => {
    const m = new Map();
    dictRows.forEach((r) => {
      const list = m.get(r.기획전명) || [];
      list.push(r.기획전번호);
      m.set(r.기획전명, list);
    });
    return m;
  }, [dictRows]);

  const selectableNos = useMemo(
    () => new Set(options.map((o) => Number(o.기획전번호)).filter(Number.isFinite)),
    [options],
  );

  /** 선택 날짜를 저장 가능(eligible) / 제외(blocked)로 분류 */
  const analysis = useMemo(() => {
    const eligible = [];
    const blocked = [];
    (Array.isArray(selection) ? selection : []).forEach((item) => {
      const priceNo = Number(item?.priceNo);
      if (!Number.isFinite(priceNo) || priceNo <= 0) {
        blocked.push({ ...item, reason: 'no-price' });
        return;
      }
      const names = splitPromoNames(item?.promo);
      const nos = [];
      let unresolved = false;
      for (const name of names) {
        const candidates = nameToNos.get(name);
        if (!candidates || candidates.length !== 1) { unresolved = true; break; }
        nos.push(candidates[0]);
      }
      if (unresolved) {
        blocked.push({ ...item, reason: 'unresolved' });
        return;
      }
      // 선택 목록에 없는 기획전(마감 등)이 걸려 있으면 저장 시 지워지므로 제외
      if (nos.some((n) => !selectableNos.has(n))) {
        blocked.push({ ...item, reason: 'locked' });
        return;
      }
      eligible.push({ ...item, priceNo, currentNos: nos });
    });
    return { eligible, blocked };
  }, [selection, nameToNos, selectableNos]);

  // 저장 가능한 날짜들의 현재 상태로 3-상태 초기값 구성
  const currentState = useMemo(() => {
    const total = analysis.eligible.length;
    const counts = new Map();
    analysis.eligible.forEach((it) => {
      it.currentNos.forEach((n) => counts.set(n, (counts.get(n) || 0) + 1));
    });
    const state = new Map();
    selectableNos.forEach((no) => {
      const c = counts.get(no) || 0;
      state.set(no, { count: c, all: total > 0 && c === total, partial: c > 0 && c < total });
    });
    return state;
  }, [analysis.eligible, selectableNos]);

  // 닫히면 다음 열기 때 다시 초기화되도록 가드를 푼다.
  useEffect(() => {
    if (!open) initializedRef.current = false;
  }, [open]);

  // 조회가 끝난 시점에 "전체 적용"인 기획전만 체크된 상태로 딱 한 번 초기화한다.
  // currentState는 부모 리렌더로도 새 Map이 되므로, 가드 없이 의존시키면
  // 사용자가 체크하던 중에 상태가 날아간다.
  useEffect(() => {
    if (!open || loading || error || initializedRef.current) return;
    const init = new Set();
    currentState.forEach((v, no) => { if (v.all) init.add(no); });
    setChecked(init);
    setTouched(new Set());
    initializedRef.current = true;
  }, [open, loading, error, currentState]);

  const toggle = (no) => {
    setTouched((prev) => new Set(prev).add(no));
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(no)) next.delete(no); else next.add(no);
      return next;
    });
  };

  const submit = async () => {
    if (analysis.eligible.length === 0) return;
    const names = options
      .filter((o) => checked.has(Number(o.기획전번호)))
      .map((o) => o.기획전명);
    const msg = names.length > 0
      ? `선택한 ${analysis.eligible.length}개 날짜의 기획전을 다음으로 통일합니다.\n\n${names.join('\n')}\n\n계속하시겠습니까?`
      : `선택한 ${analysis.eligible.length}개 날짜의 기획전 매핑을 모두 해제합니다.\n\n계속하시겠습니까?`;
    if (!window.confirm(msg)) return;
    try {
      setSaving(true);
      const promotionNos = Array.from(checked);
      await mapPricesToPromotions({
        productNo,
        priceNos: analysis.eligible.map((it) => it.priceNo),
        promotionNos,
      });

      // 대리점 포털(jn_crs) 노출용 Supabase 미러. ERP 저장이 원본이므로 실패해도
      // 되돌리지 않고 안내만 한다 — 다시 저장하면 재시도된다.
      const { companyNo } = getUserInfo() || {};
      const mirrored = await mirrorPriceMapping({
        promotionNos,
        departures: analysis.eligible.map((it) => ({ 가격번호: it.priceNo, 출발일: it.date })),
        companyNo,
        productNo,
        productName,
      });

      if (!mirrored.ok) {
        console.warn('기획전 매핑 미러링 실패:', mirrored.error);
        window.alert(
          '매핑은 저장되었지만 대리점 포털 노출용 동기화에 실패했습니다.\n'
          + '다시 저장하면 재시도됩니다.',
        );
      } else if (mirrored.skippedPromotionNos?.length > 0) {
        // 소유가 증명되지 않은 기획전 — 본사 공용이거나, 기획전 관리에서 아직
        // 한 번도 저장하지 않아 미러에 헤더가 없는 경우.
        const names = options
          .filter((o) => mirrored.skippedPromotionNos.includes(Number(o.기획전번호)))
          .map((o) => o.기획전명);
        window.alert(
          'ERP 매핑은 저장되었습니다.\n\n'
          + `다만 다음 기획전은 대리점 포털에 노출되지 않습니다:\n${names.join('\n')}\n\n`
          + '내 업체가 등록한 기획전이라면 [기획전 관리]에서 한 번 저장한 뒤 다시 매핑하세요.',
        );
      }

      onSaved && await onSaved({
        dates: analysis.eligible.map((it) => it.date),
        promoNames: names,
      });
      onClose && onClose();
    } catch (e) {
      window.alert(e?.message || '기획전 매핑 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const total = analysis.eligible.length;
  const blockedByReason = analysis.blocked.reduce((acc, b) => {
    (acc[b.reason] = acc[b.reason] || []).push(b);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={() => !saving && onClose && onClose()} />
      <div className="relative bg-white rounded-lg shadow-xl w-[min(34rem,94vw)] max-h-[88vh] flex flex-col">
        <div className="px-6 py-4 border-b">
          <h2 className="text-base font-semibold">기획전 매핑</h2>
          <p className="text-xs text-gray-500 mt-1">
            선택한 날짜 {selection.length}건 중 <b className="text-gray-700">{total}건</b>에 적용됩니다.
            {analysis.blocked.length > 0 && <> · 제외 {analysis.blocked.length}건</>}
          </p>
        </div>

        {loading && <div className="px-6 py-16 text-center text-gray-500">불러오는 중...</div>}
        {!loading && error && <div className="px-6 py-16 text-center text-red-600">{error}</div>}

        {!loading && !error && (
          <div className="px-6 py-4 overflow-auto space-y-4">
            {options.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-500">
                선택 가능한 진행중 기획전이 없습니다.
                <br />
                <span className="text-xs">설정 &gt; 기획전 관리에서 먼저 기획전을 등록하세요.</span>
              </div>
            )}

            {options.length > 0 && (
              <ul className="border rounded-lg divide-y divide-gray-100">
                {options.map((o) => {
                  const no = Number(o.기획전번호);
                  const st = currentState.get(no) || { count: 0, all: false, partial: false };
                  const isChecked = checked.has(no);
                  return (
                    <li key={no}>
                      <label className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 ${total === 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={isChecked}
                          // 부분 적용은 '-'로 표시하되, 사용자가 직접 끈 항목은 확정된 해제로 본다.
                          // (안 그러면 명시적으로 해제해도 '-'로 되돌아가 의도가 흐려진다)
                          ref={(el) => { if (el) el.indeterminate = !isChecked && st.partial && !touched.has(no); }}
                          onChange={() => toggle(no)}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-gray-900 truncate">{o.기획전명}</span>
                          <span className="block text-[11px] text-gray-500">
                            {o.시작일} ~ {o.종료일}
                          </span>
                        </span>
                        <span className="text-[11px] whitespace-nowrap text-right">
                          <span className="block">
                            {st.all && <span className="text-emerald-700 font-medium">전체 적용 ({st.count}/{total})</span>}
                            {st.partial && <span className="text-amber-700 font-medium">부분 적용 ({st.count}/{total})</span>}
                            {!st.all && !st.partial && <span className="text-gray-400">미적용</span>}
                          </span>
                          {/* 저장 후 결과가 현재와 달라지는 항목만 변경 예정을 명시 */}
                          {isChecked !== st.all && (
                            <span className={`block ${isChecked ? 'text-blue-700' : 'text-red-700'}`}>
                              → {isChecked ? `전체 적용 (${total}/${total})` : '해제 (0/' + total + ')'}
                            </span>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            {total > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900">
                저장하면 선택한 <b>{total}개 날짜의 기획전 소속이 모두 동일해집니다.</b>
                {' '}부분 적용 항목은 전체 적용 또는 전체 해제만 가능합니다.
                {checked.size === 0 && <><br /><b>현재 아무 기획전도 선택되지 않아 매핑이 전부 해제됩니다.</b></>}
              </div>
            )}

            {analysis.blocked.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-[12px] font-medium text-gray-700"
                  onClick={() => setShowBlocked((v) => !v)}
                >
                  <span>제외된 날짜 {analysis.blocked.length}건 — 이 화면에서 변경할 수 없습니다</span>
                  <span className="text-gray-400">{showBlocked ? '접기' : '보기'}</span>
                </button>
                {showBlocked && (
                  <div className="mt-2 space-y-2">
                    {Object.entries(blockedByReason).map(([reason, items]) => (
                      <div key={reason}>
                        <div className="text-[11px] font-medium text-gray-600">
                          {BLOCK_LABEL[reason] || reason} · {items.length}건
                        </div>
                        <div className="mt-0.5 text-[11px] text-gray-500 leading-relaxed">
                          {items.map((it) => it.date).join(', ')}
                        </div>
                      </div>
                    ))}
                    <p className="text-[11px] text-gray-500 pt-1 border-t border-gray-200">
                      마감된 기획전에 속한 날짜는 저장 시 매핑이 사라지는 것을 막기 위해 제외했습니다.
                      변경이 필요하면 ERP에서 처리하세요.
                    </p>
                  </div>
                )}
              </div>
            )}

            {total === 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[12px] text-red-800">
                적용할 수 있는 날짜가 없습니다. 선택한 날짜가 모두 제외 대상입니다.
              </div>
            )}
          </div>
        )}

        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <button type="button" className="px-4 py-2 border rounded-lg" onClick={() => onClose && onClose()} disabled={saving}>취소</button>
          <button
            type="button"
            className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            onClick={submit}
            disabled={saving || loading || !!error || total === 0}
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromotionMappingModal;
